import { useMemo } from 'react';
import { useHartfordLevel, useStrategy } from '@/store/selectors';
import { NomogramSource } from '@/types';
import { HARTFORD_BOUNDARIES, ALTERNATIVE_BOUNDARIES, type BoundaryLine } from '@/config/nomogramBoundaries';
import { nomogramLabel } from '@/utils/format';

const W = 480;
const H_PX = 360;
const PAD = 48;
const X_MIN = 6;
const X_MAX = 14;
const Y_MIN = 0;
const Y_MAX = 14;

function scaleX(t: number): number {
  return PAD + ((t - X_MIN) / (X_MAX - X_MIN)) * (W - 2 * PAD);
}
function scaleY(y: number): number {
  return H_PX - PAD - ((y - Y_MIN) / (Y_MAX - Y_MIN)) * (H_PX - 2 * PAD);
}
function pathFromLine(line: BoundaryLine): string {
  return line.map(([t, y], i) => `${i === 0 ? 'M' : 'L'} ${scaleX(t).toFixed(1)} ${scaleY(y).toFixed(1)}`).join(' ');
}

const ZONE_FILL: Record<string, string> = {
  q24: 'var(--amg-nomo-q24)',
  q36: 'var(--amg-nomo-q36)',
  q48: 'var(--amg-nomo-q48)',
  exit: 'var(--amg-nomo-exit)',
};

/**
 * SVG Hartford nomogram with patient level plotted (PSD §8.2 / TODO Task 1.28).
 *
 * Rendered in two contexts: the Calculator Charts tab (alongside the
 * concentration curve) and the Levels screen (next to level entry). This is
 * intentional: each context pairs the nomogram with different supporting UI.
 * Both instances read the same store (useHartfordLevel / useStrategy), so they
 * share a single source of truth and cannot drift in state — the component is
 * the shared container. (Critique: "decide whether the Calculator copy earns
 * its maintenance cost" — it does, and the cost is zero divergence.)
 */
export function NomogramPlot(): JSX.Element | null {
  const hartfordLevel = useHartfordLevel();
  const strategy = useStrategy();

  const boundaries = useMemo(() => {
    const src = strategy.nomogramSource;
    return src === NomogramSource.Hartford ? HARTFORD_BOUNDARIES : ALTERNATIVE_BOUNDARIES[src];
  }, [strategy.nomogramSource]);

  // Zone polygons (fill between boundary lines)
  const zones = useMemo(() => {
    return [
      { fill: ZONE_FILL.exit, path: pathFromLine(boundaries.q48Exit) + ` L ${scaleX(X_MAX)} ${scaleY(Y_MAX)} L ${scaleX(X_MIN)} ${scaleY(Y_MAX)} Z` },
      { fill: ZONE_FILL.q48, path: pathFromLine(boundaries.q36q48) + ` L ${scaleX(X_MAX)} ${scaleY(boundaries.q48Exit[boundaries.q48Exit.length - 1][1])} L ${scaleX(X_MIN)} ${scaleY(boundaries.q48Exit[0][1])} Z` },
      { fill: ZONE_FILL.q36, path: pathFromLine(boundaries.q24q36) + ` L ${scaleX(X_MAX)} ${scaleY(boundaries.q36q48[boundaries.q36q48.length - 1][1])} L ${scaleX(X_MIN)} ${scaleY(boundaries.q36q48[0][1])} Z` },
      { fill: ZONE_FILL.q24, path: `M ${scaleX(X_MIN)} ${scaleY(0)} L ${scaleX(X_MAX)} ${scaleY(0)} L ${scaleX(X_MAX)} ${scaleY(boundaries.q24q36[boundaries.q24q36.length - 1][1])} L ${scaleX(X_MIN)} ${scaleY(boundaries.q24q36[0][1])} Z` },
    ];
  }, [boundaries]);

  const levelPoint = hartfordLevel
    ? { x: scaleX(hartfordLevel.drawTimeHr), y: scaleY(hartfordLevel.valueMg), inWindow: hartfordLevel.drawTimeHr >= X_MIN && hartfordLevel.drawTimeHr <= X_MAX }
    : null;

  const axisTicks = [6, 8, 10, 12, 14];
  const yTicks = [0, 2, 4, 6, 8, 10, 12, 14];

  return (
    <section className="panel panel-result" aria-label="Nomogram plot" data-testid="nomogram-plot">
      <h2 className="panel-title">Nomogram — {nomogramLabel(strategy.nomogramSource)}</h2>
      <svg className="nomogram-svg" viewBox={`0 0 ${W} ${H_PX}`} role="img" aria-label="Hartford nomogram chart">
        {/* Zones */}
        {zones.map((z, i) => (
          <path key={i} d={z.path} fill={z.fill} stroke="none" />
        ))}

        {/* Boundary lines */}
        <path d={pathFromLine(boundaries.q24q36)} fill="none" stroke="var(--amg-brand-primary)" strokeWidth={1.5} />
        <path d={pathFromLine(boundaries.q36q48)} fill="none" stroke="var(--amg-brand-primary)" strokeWidth={1.5} />
        <path d={pathFromLine(boundaries.q48Exit)} fill="none" stroke="var(--amg-status-danger)" strokeWidth={1.5} strokeDasharray="4 2" />

        {/* Zone labels */}
        <text x={scaleX(7)} y={scaleY(1.5)} fontSize="10" fill="var(--amg-text-muted)">q24h</text>
        <text x={scaleX(7)} y={scaleY(5.5)} fontSize="10" fill="var(--amg-text-muted)">q36h</text>
        <text x={scaleX(7)} y={scaleY(8)} fontSize="10" fill="var(--amg-text-muted)">q48h</text>
        <text x={scaleX(7)} y={scaleY(12)} fontSize="10" fill="var(--amg-status-danger)">EXIT</text>

        {/* Axes */}
        <line x1={PAD} y1={H_PX - PAD} x2={W - PAD} y2={H_PX - PAD} stroke="var(--amg-text-secondary)" strokeWidth={1} />
        <line x1={PAD} y1={PAD} x2={PAD} y2={H_PX - PAD} stroke="var(--amg-text-secondary)" strokeWidth={1} />

        {/* X ticks */}
        {axisTicks.map((t) => (
          <g key={t}>
            <line x1={scaleX(t)} y1={H_PX - PAD} x2={scaleX(t)} y2={H_PX - PAD + 4} stroke="var(--amg-text-secondary)" />
            <text x={scaleX(t)} y={H_PX - PAD + 16} fontSize="10" textAnchor="middle" fill="var(--amg-text-secondary)">{t}h</text>
          </g>
        ))}

        {/* Y ticks */}
        {yTicks.map((y) => (
          <g key={y}>
            <line x1={PAD - 4} y1={scaleY(y)} x2={PAD} y2={scaleY(y)} stroke="var(--amg-text-secondary)" />
            <text x={PAD - 8} y={scaleY(y) + 3} fontSize="10" textAnchor="end" fill="var(--amg-text-secondary)">{y}</text>
          </g>
        ))}

        {/* Axis labels */}
        <text x={(W) / 2} y={H_PX - 8} fontSize="11" textAnchor="middle" fill="var(--amg-text-primary)">
          Hours after infusion start
        </text>
        <text x={14} y={H_PX / 2} fontSize="11" textAnchor="middle" fill="var(--amg-text-primary)" transform={`rotate(-90 14 ${H_PX / 2})`}>
          Level (mg/L)
        </text>

        {/* Patient level point */}
        {levelPoint && levelPoint.inWindow && (
          <g>
            <circle cx={levelPoint.x} cy={levelPoint.y} r={6} fill="var(--amg-brand-primary)" stroke="var(--amg-surface-input)" strokeWidth={2} />
            <text x={levelPoint.x + 10} y={levelPoint.y - 8} fontSize="11" fill="var(--amg-text-primary)" fontWeight="bold">
              {hartfordLevel!.valueMg.toFixed(1)} mg/L @ {hartfordLevel!.drawTimeHr}h
            </text>
          </g>
        )}
        {levelPoint && !levelPoint.inWindow && (
          <text x={W / 2} y={H_PX / 2} fontSize="12" textAnchor="middle" fill="var(--amg-status-danger)">
            Level outside 6–14h window
          </text>
        )}
      </svg>
      {!levelPoint && (
        <div className="hint-text text-center">
          Enter a Hartford random level (Levels tab) to plot on the nomogram.
        </div>
      )}
      <p className="hint-text">Hartford nomogram: a random level drawn 6–14 h post-infusion start is classified into q24h, q36h, q48h, or exit-EI dosing zones. The plotted dot shows the patient's measured level.</p>
    </section>
  );
}
